<template>
  <div class="relative w-full">
    <div class="mb-4 text-center">
        <h3 class="font-black">Message Preview</h3>
        <p v-if="contactLabel" class="text-sm text-gray-700">
            Showing preview for contact <span class="font-black">{{ contactLabel }}</span>
        </p>
    </div>
    <div class="message-preview-wrapper">
      <div class="message-preview-wrapper-inner" ref="messagePreviewWrapperInner">
        <div class="message-preview">
            <img
                v-if="media"
                :src="media"
                class="block w-full rounded-lg mb-3"
                alt="preview of image attachment"
            >
            <div v-if="messagePreview" v-html="messagePreview" class="p-3 text-white bg-blue-500 rounded-lg"></div>
            <div v-else class="p-3 text-gray-700 bg-gray-100 rounded">
                Enter a message to show preview...
            </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import moment from 'moment';
export default {
    props: {
        message: {
            type: String,
            default: '',
        },
        contact: {
            type: Object,
            default: null,
        },
        group: {
            type: Object,
            default: null,
        },
        media: {
            type: String,
            default: null,
        }
    },
  data() {
    return {
      exampleContact: null,
      contactWithCustomFields: null,
    };
  },
  computed: {
    contactLabel() {

        if (this.contact) {
            return this.contact.first_name ?
                `${this.contact.first_name} ${this.contact.last_name}` :
                this.contact.phone_number;
        }

        if (this.exampleContact) {
            return this.exampleContact.first_name ?
                `${this.exampleContact.first_name} ${this.exampleContact.last_name}` :
                this.exampleContact.phone_number;
        }


    },
    fieldsMap() {
      let defaultFields = {
        'firstName': 'first_name',
        'lastName': 'last_name',
      };

      let customFields = this.$page.prop.custom_fields.data.map(field => {
        return {
          [field.tag]: field.name,
        };
      });

    },
    messagePreview() {
        // Original patterns for standard fields and links
        const standardPattern = /{\s*([a-zA-Z]+)(\s+(default)\=[\'\"]([^\'\"\n]*)[\'\"])?\s*}/g;
        const linkPattern = /\{\s?link url="([^"]*)"(?:\s?track)?\s?\}/g;
        // Updated pattern for custom fields to use names instead of IDs
        const customFieldPattern = /{\s*"([^"]*)"\s*(\s+(default)\=[\'\"]([^\'\"\n]*)[\'\"])?\s*}/g;

        const fieldsMap = {
            'firstName': 'first_name',
            'lastName': 'last_name',
        };

        // Define address components for lookup
        const addressComponents = {
            'Street Line 1': 'street1',
            'Street Line 2': 'street2',
            'City': 'city',
            'State': 'state',
            'Postal Code': 'postal_code'
        };

        // Get all matches
        const standardMatches = [...this.message.matchAll(new RegExp(standardPattern))];
        const linkMatches = [...this.message.matchAll(new RegExp(linkPattern))];
        const customFieldMatches = [...this.message.matchAll(new RegExp(customFieldPattern))];

        var newMessage = this.message.replace(/\n/g, '<br />');

        // Helper function to get custom field by name
        const getCustomFieldByName = (fieldName) => {
            if (!this.$page.props.custom_fields.data) return null;

            const customField = this.$page.props.custom_fields.data.find(field =>
                field.name === fieldName
            );

            return customField;
        };

        // Helper function to get address field
        const getAddressField = () => {
            if (!this.$page.props.custom_fields.data) return null;

            return this.$page.props.custom_fields.data.find(field =>
                field.type === 'Address'
            );
        };

        // Helper function to get custom field value considering if it's an address component
        const getCustomFieldValue = (fieldName) => {
            if (!this.contactWithCustomFields || !this.contactWithCustomFields.custom_fields) {
                return null;
            }

            // Check if this is an address component
            if (addressComponents.hasOwnProperty(fieldName)) {
                const addressField = getAddressField();
                if (!addressField) return null;

                const addressValue = this.contactWithCustomFields.custom_fields[addressField.tra_id];
                if (!addressValue) return null;

                return addressValue[addressComponents[fieldName]] || null;
            }

            // Regular custom field
            const customField = getCustomFieldByName(fieldName);
            if (!customField) return null;

            return this.contactWithCustomFields.custom_fields[customField.tra_id] || null;
        };

        // Process standard fields
        standardMatches.forEach(match => {
            const fieldKey = fieldsMap.hasOwnProperty(match[1]) ?
                fieldsMap[match[1]] : null;
            const defaultValue = match[4];
            const fieldValue = (
                this.contact &&
                fieldKey &&
                this.contact[fieldKey] &&
                this.contact[fieldKey].length > 0
            ) ? this.contact[fieldKey] : (
                this.group &&
                fieldKey &&
                this.exampleContact &&
                this.exampleContact[fieldKey] &&
                this.exampleContact[fieldKey].length > 0
            ) ? this.exampleContact[fieldKey] : null;

            const replaceWith = fieldValue || defaultValue || fieldKey || null;
            const helpText = 'This is a variable. It will be replaced with the corresponding field for each contact. If the field is not available, it will look for a default value. If no default is found, the variable will be omitted.';

            const variableHtml = replaceWith ?
                `<span class="message-preview-variable" title="${helpText}">${replaceWith}</span>` :
                '';

            newMessage = newMessage.replace(match[0], variableHtml);
        });

        // Process custom fields
        customFieldMatches.forEach(match => {
            const fieldName = match[1]; // Gets the field name
            const defaultValue = match[4]; // Gets the default value if present

            // Check if it's an address component
            if (addressComponents.hasOwnProperty(fieldName)) {
                const fieldValue = getCustomFieldValue(fieldName);
                const replaceWith = fieldValue || defaultValue || null;
                const helpText = `This is an address ${addressComponents[fieldName]} field. It will be replaced with the corresponding address component for each contact.`;

                const variableHtml = replaceWith ?
                    `<span class="message-preview-variable" title="${helpText}">${replaceWith}</span>` :
                    '';

                newMessage = newMessage.replace(match[0], variableHtml);
                return;
            }

            // Handle regular custom fields
            const customField = getCustomFieldByName(fieldName);
            if (!customField) {
                newMessage = newMessage.replace(match[0], '');
                return;
            }

            const fieldValue = getCustomFieldValue(fieldName);
            let replaceWith = fieldValue || defaultValue || null;
            let helpText = 'This is a custom field variable. It will be replaced with the corresponding custom field value for each contact.';

            // Special handling for URL type
            let variableHtml = '';
            if (customField.type === 'URL' && replaceWith) {
                helpText = 'This is a link placeholder. It will be replaced with a shortened link that will redirect recipients to the original link you entered.';
                variableHtml = `<span class="message-preview-variable" title="${helpText}">txts.pt/XXXXXXX</span>`;
            } else if (customField.type === 'Date' && replaceWith) {
                let parsedDate = moment(replaceWith, 'YYYY-MM-DD', true);
                if (parsedDate.isValid()) {
                    replaceWith = parsedDate.format('MMM D, YYYY');
                }
                variableHtml = `<span class="message-preview-variable" title="${helpText}">${replaceWith}</span>`;
            }
            else if (replaceWith) {
                variableHtml = `<span class="message-preview-variable" title="${helpText}">${replaceWith}</span>`;
            }

            newMessage = newMessage.replace(match[0], variableHtml);
        });

        // Process links
        linkMatches.forEach(match => {
            const replaceWith = "txts.pt/XXXXXXX";
            const helpText = 'This is a link placeholder. It will be replaced with a shortened link that will redirect recipients to the original link you entered.';
            const variableHtml = replaceWith ?
                `<span class="message-preview-variable" title="${helpText}">${replaceWith}</span>` :
                '';

            newMessage = newMessage.replace(match[0], variableHtml);
        });

        // Add trial watermark if organization is on trial and there's actual message content
        if (this.$page.props.active_trial && this.message && this.message.trim()) {
            newMessage += '<br /><br />Sent via TextSpot Trial';
        }

        return newMessage;
    }
  },

  methods: {
    scrollToBottom () {
      if (this.$refs.messagePreviewWrapperInner) {
        this.$refs.messagePreviewWrapperInner.scrollTo({
          top: this.$refs.messagePreviewWrapperInner.scrollHeight,
          behavior: 'smooth',
        })
      }
    },
  },
  watch: {
    message() {
      this.scrollToBottom();
    },
    contact() {
        if (!this.contact) {
            this.exampleContact = null;
            return;
        }

        axios.get(route('api.find.contact', this.contact.id)).then(
            response => {
                this.contactWithCustomFields = response.data;
            }
        );
    },
    group: {
        async handler() {
            if (!this.group) {
                this.exampleContact = null;
                return;
            }
            const response = await axios.get(route('groups.firstContact', this.group.tra_id));
            this.exampleContact = response.data;
        },
        immediate: true,
        deep: true,
    }
  },
};
</script>
<style>
.message-preview-wrapper {
  @apply mx-auto px-9 pb-4 relative bg-no-repeat bg-top max-w-xs;
  padding-top: 70px;
  background-image: url("/images/iphone-wrapper-cropped.png");
  background-size: 100%;
  height: 90%;
}

.message-preview-wrapper-inner {
  @apply h-full relative pt-3 pb-5 rounded-lg overflow-auto;
  min-height: 204px;
  max-height: 400px;
}

.message-preview-wrapper:before {
  @apply absolute inset-x-8 top-16 h-6 z-10;
  content: "";
  background: linear-gradient(180deg, white, transparent);
}

.message-preview-wrapper:after {
  @apply absolute inset-x-0 bottom-0 h-12 z-10;
  content: "";
  background: linear-gradient(0deg, white, transparent);
}

.message-preview {
  @apply flex flex-col items-start justify-end;
  min-height: 180px;
}

@media (min-width: theme("screens.lg")) {
  .message-preview-wrapper-inner {
    min-height: 284px;
  }

  .message-preview {
    min-height: 240px;
  }
}

.message-preview-variable {
  @apply bg-blue-700 shadow leading-loose px-2 py-1 font-black;
  cursor: help;
}
</style>
